import { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import CheckGlucose from './pages/CheckGlucose';
import InjectInsulin from './pages/InjectInsulin';
import HistoryListPage from './pages/HistoryList';
import Dashboard from './pages/Dashboard';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import CompleteProfile from './pages/CompleteProfile';
import Header from './components/Header';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { InstallPromptModal, BeforeInstallPromptEvent } from './components/InstallPromptModal';

function AppShell() {
  const location = useLocation();
  const isRoot = location.pathname === '/' || location.pathname === '';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/complete-profile';

  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => {
    try {
      return localStorage.getItem('install_dismissed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    const matchMedia = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (evt: MediaQueryListEvent) => {
      if (evt.matches) {
        setIsInstalled(true);
      }
    };

    // Modern browsers
    try {
      matchMedia.addEventListener('change', handleDisplayModeChange);
    } catch {
      // Fallback for older browsers
      matchMedia.addListener(handleDisplayModeChange);
    }

    if (matchMedia.matches || (window.navigator as any).standalone === true) {
      setIsInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      try {
        matchMedia.removeEventListener('change', handleDisplayModeChange);
      } catch {
        matchMedia.removeListener(handleDisplayModeChange);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) {
      return;
    }
    try {
      await installPromptEvent.prompt();
      const choice = await installPromptEvent.userChoice;
      if (choice.outcome === 'accepted') {
        setInstallPromptEvent(null);
      }
    } catch {
      setInstallPromptEvent(null);
    }
  };

  const handleDismissInstall = () => {
    setInstallDismissed(true);
    try {
      localStorage.setItem('install_dismissed', 'true');
    } catch {}
  };

  // Show modal if not installed, not dismissed, and (we have an event OR we want to show instructions)
  // But usually we only show if we have an event OR if it's explicitly requested.
  // The requirement is "modal when first load App".
  // So we show it if !isInstalled && !installDismissed.
  const showInstallModal = !isInstalled && !installDismissed;

  return (
    <>
      {!isAuthPage && <Header title="Insulin Injection" enableBack={!isRoot} />}
      <main className="!mt-0">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute requireProfile={false}>
                <CompleteProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-glucose"
            element={
              <ProtectedRoute>
                <CheckGlucose />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inject-insulin"
            element={
              <ProtectedRoute>
                <InjectInsulin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <HistoryListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <About />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      <InstallPromptModal
        isVisible={showInstallModal}
        installPromptEvent={installPromptEvent}
        onInstall={handleInstallClick}
        onDismiss={handleDismissInstall}
      />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
